import { http } from '@/shared/api/http';
import type { DeepRequired, Schema, WithOptional } from '@/shared/api';

/**
 * Вложение сообщения чата. С 08.08.2026 бэк отдаёт одну и ту же модель во всех
 * четырёх чатах (задача/проект/отдел/личка), поэтому тип общий.
 * url — проксирующая ручка вида "/tasks-module-back/files/{id}/raw".
 */
export type FileAttachment = DeepRequired<
  Schema<'tasks_module_internal_models.FileAttachment'>
>;

/** Compact preview of the message a reply points to. */
export type ChatReplyPreview = WithOptional<
  DeepRequired<Schema<'tasks_module_internal_models.ChatReplyPreview'>>,
  'isVoiceNote' | 'fileName'
>;

/** One emoji's reaction tally on a message — the set of users who reacted. */
export type ReactionGroup = DeepRequired<
  Schema<'tasks_module_internal_models.ReactionGroup'>
>;

export interface BackendChatMessage {
  id: number;
  chatId: number;
  authorId: number;
  date: string; // ISO with offset, e.g. "2026-05-26T10:00:00+06:00"
  text: string;
  isSystem: boolean;
  /** Emoji reactions grouped by emoji. Absent (omitempty) when none. */
  reactions?: ReactionGroup[];
  /** Present when this message is a reply to another. */
  replyTo?: ChatReplyPreview;
  /** Native Bitrix voice note (isVoiceNote flag from IM). */
  isVoiceNote?: boolean;
  voiceUrl?: string;
  voiceDurationSec?: number;
  voiceTranscript?: string;
  attachments?: FileAttachment[];
  /** True once the message was edited (Bitrix EDIT_DATE present). */
  isEdited?: boolean;
  /** True while the message is pinned (module-only; not reflected in Bitrix). */
  isPinned?: boolean;
  /** Mentioned user ids (for highlight); empty/absent when none. */
  mentions?: number[];
  /** True when the message mentions everyone in the chat. */
  mentionAll?: boolean;
}

export interface ChatPermissions {
  /** "creator" | "responsible" | "auditor" | "accomplice" | "observer" */
  role: string;
  canWrite: boolean;
}

/** A participant's read pointer — they've read all messages up to this id. */
export type ChatReadPosition = DeepRequired<
  Schema<'tasks_module_internal_models.ChatReadPosition'>
>;

export type TaskChatResponse = WithOptional<
  DeepRequired<Schema<'tasks_module_internal_models.TaskChatResponse'>>,
  'pinnedMessages' | 'helpModeActive' | 'reads'
>;

/** GET /tasks/{id}/chat — load history (≤50 last) + permissions.
 *  Also registers the IM bot in the chat (server-side, fire-and-forget),
 *  which is required before opening the WebSocket. */
export async function getTaskChat(
  taskId: string | number
): Promise<TaskChatResponse> {
  const { data } = await http.get<TaskChatResponse>(
    `/tasks/${encodeURIComponent(taskId)}/chat`
  );
  return data;
}

/** A colleague who can be summoned for help. */
export type HelpUser = DeepRequired<
  Schema<'tasks_module_internal_models.HelpUser'>
>;

export type HelpDepartment = DeepRequired<
  Schema<'tasks_module_internal_models.HelpDepartment'>
>;

export type HelpUsersResponse = DeepRequired<
  Schema<'tasks_module_internal_models.HelpUsersResponse'>
>;

/** GET /tasks/{id}/chat/help/users — active colleagues grouped by department,
 *  with online status, for the "call for help" modal. */
export async function getHelpUsers(
  taskId: string | number
): Promise<HelpUsersResponse> {
  const { data } = await http.get<HelpUsersResponse>(
    `/tasks/${encodeURIComponent(taskId)}/chat/help/users`
  );
  return data;
}

/** POST /tasks/{id}/chat/help/start — activate Help mode (creator/responsible
 *  only). Optionally pass `userIDs` to add them as accomplices and post a
 *  mention message into the chat. */
export async function startHelpMode(
  taskId: string | number,
  userIDs?: number[]
): Promise<void> {
  await http.post(
    `/tasks/${encodeURIComponent(taskId)}/chat/help/start`,
    userIDs && userIDs.length > 0 ? { userIDs } : undefined
  );
}

/** POST /tasks/{id}/chat/help/stop — deactivate Help mode. */
export async function stopHelpMode(taskId: string | number): Promise<void> {
  await http.post(`/tasks/${encodeURIComponent(taskId)}/chat/help/stop`);
}

/** POST /tasks/{id}/chat/read — mark messages read up to `lastReadMessageId`
 *  for the current user. Server broadcasts a `chat_read` WS event. */
export async function markChatRead(
  taskId: string | number,
  lastReadMessageId: number
): Promise<void> {
  await http.post(`/tasks/${encodeURIComponent(taskId)}/chat/read`, {
    lastReadMessageId,
  });
}

export interface ChatMessagesPage {
  messages: BackendChatMessage[];
  hasMore: boolean;
}

/** GET /tasks/{id}/chat/messages?before_id=X&limit=N
 *  Load older history when the user scrolls up. Returns up to `limit` messages
 *  with id strictly < before_id, sorted ASC (old → new). */
export async function getChatMessagesBefore(
  taskId: string | number,
  beforeId: number,
  limit = 50
): Promise<ChatMessagesPage> {
  const { data } = await http.get<ChatMessagesPage>(
    `/tasks/${encodeURIComponent(taskId)}/chat/messages`,
    { params: { before_id: beforeId, limit } }
  );
  return data;
}

/** GET /tasks/{id}/chat/messages/search?q=&before_id=&limit=
 *  Поиск по тексту сообщений чата задачи (Bitrix im.dialog.messages.search,
 *  q ≥ 3 символа). Форма ответа как у обычной истории; reads не используем.
 *  before_id не передаём на первую страницу, дальше — id самого старого из
 *  предыдущего ответа. */
export async function searchTaskChatMessages(
  taskId: string | number,
  q: string,
  beforeId?: number,
  limit = 50
): Promise<ChatMessagesPage> {
  const { data } = await http.get<ChatMessagesPage>(
    `/tasks/${encodeURIComponent(taskId)}/chat/messages/search`,
    { params: { q, ...(beforeId ? { before_id: beforeId } : {}), limit } }
  );
  return { messages: data?.messages ?? [], hasMore: !!data?.hasMore };
}

/** POST /tasks/{id}/chat/messages — send a new message.
 *  The server will deliver it to bot → broadcast via WS to all subscribers. */
export async function sendChatMessage(
  taskId: string | number,
  text: string,
  replyToId?: number,
  mentions?: number[],
  mentionAll?: boolean
): Promise<number | undefined> {
  const { data } = await http.post<{ messageId?: number }>(
    `/tasks/${encodeURIComponent(taskId)}/chat/messages`,
    {
      text,
      ...(replyToId ? { replyToId } : {}),
      mentions: mentions ?? [],
      mentionAll: Boolean(mentionAll),
    }
  );
  return data?.messageId;
}

/** POST /tasks/{id}/voice — upload an audio blob; backend stores it on Bitrix
 *  Disk and sends a native voice note (isVoiceNote) into the task IM chat. */
export async function sendVoiceMessage(
  taskId: string | number,
  blob: Blob,
  filename: string,
  replyToId?: number
): Promise<void> {
  const formData = new FormData();
  formData.append('file', blob, filename);
  if (replyToId) {
    formData.append('reply_to_id', String(replyToId));
  }
  await http.post(`/tasks/${encodeURIComponent(taskId)}/voice`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
}

/** POST /tasks/{id}/chat/files — upload one or more files as a single chat
 *  message (repeated `files` field). The caption/reply ride on the message.
 *  Backend stores them on Bitrix Disk and sends via im.disk.file.commit. */
export async function sendChatFile(
  taskId: string | number,
  files: File[],
  replyToId?: number,
  text?: string,
  sendAsFile?: boolean
): Promise<void> {
  const formData = new FormData();
  for (const file of files) {
    formData.append('files', file, file.name);
  }
  if (replyToId) {
    formData.append('reply_to_id', String(replyToId));
  }
  if (text) {
    formData.append('text', text);
  }
  if (sendAsFile) {
    formData.append('send_as_file', '1');
  }
  await http.post(`/tasks/${encodeURIComponent(taskId)}/chat/files`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
}

/** PUT /tasks/{id}/chat/messages/{messageId} — edit own message text. */
export async function editChatMessage(
  taskId: string | number,
  messageId: number,
  text: string,
  mentions?: number[],
  mentionAll?: boolean
): Promise<void> {
  await http.put(
    `/tasks/${encodeURIComponent(taskId)}/chat/messages/${messageId}`,
    { text, mentions: mentions ?? [], mentionAll: Boolean(mentionAll) }
  );
}

/** DELETE /tasks/{id}/chat/messages/{messageId} — delete own message. */
export async function deleteChatMessage(
  taskId: string | number,
  messageId: number
): Promise<void> {
  await http.delete(
    `/tasks/${encodeURIComponent(taskId)}/chat/messages/${messageId}`
  );
}

/** Where a forwarded message should land. `targetEntityTypeId` is required
 *  when `targetType === 'project'` (the smart-process entity type, e.g. 1084). */
export interface ForwardTarget {
  targetType: 'task' | 'project';
  targetId: number;
  targetEntityTypeId?: number;
}

export type ForwardMessageResponse = DeepRequired<
  Schema<'tasks_module_internal_models.ForwardMessageResponse'>
>;

export interface BulkDeleteResult {
  deletedIds: number[];
  failedIds: number[];
}

/** POST /tasks/{id}/chat/messages/{messageId}/pin — pin a message. Allowed for
 *  any participant except observers. Broadcasts `message_pinned` over WS. */
export async function pinChatMessage(
  taskId: string | number,
  messageId: number
): Promise<void> {
  await http.post(
    `/tasks/${encodeURIComponent(taskId)}/chat/messages/${messageId}/pin`
  );
}

/** DELETE /tasks/{id}/chat/messages/{messageId}/pin — unpin a message.
 *  Broadcasts `message_unpinned` ({ id }) over WS. */
export async function unpinChatMessage(
  taskId: string | number,
  messageId: number
): Promise<void> {
  await http.delete(
    `/tasks/${encodeURIComponent(taskId)}/chat/messages/${messageId}/pin`
  );
}

/** POST /tasks/{id}/chat/messages/{messageId}/forward — forward a message into
 *  another task/project chat (marked "Переслано от"). */
export async function forwardChatMessage(
  taskId: string | number,
  messageId: number,
  target: ForwardTarget
): Promise<ForwardMessageResponse> {
  const { data } = await http.post<ForwardMessageResponse>(
    `/tasks/${encodeURIComponent(taskId)}/chat/messages/${messageId}/forward`,
    target
  );
  return data;
}

/** POST /tasks/{id}/chat/messages/{messageId}/add-result — mark a message
 *  as a task result. */
export async function addChatMessageAsResult(
  taskId: string | number,
  messageId: number
): Promise<void> {
  await http.post(
    `/tasks/${encodeURIComponent(taskId)}/chat/messages/${messageId}/add-result`
  );
}

export interface ToggleReactionResponse {
  messageId: number;
  reactions: ReactionGroup[];
}

/** POST /tasks/{id}/chat/messages/{messageId}/reactions — toggle an emoji
 *  reaction (adds it, or removes it if the user already set it). Returns the
 *  message's full reaction state; a `reaction_updated` WS echo follows. */
export async function toggleChatReaction(
  taskId: string | number,
  messageId: number,
  emoji: string
): Promise<ToggleReactionResponse> {
  const { data } = await http.post<ToggleReactionResponse>(
    `/tasks/${encodeURIComponent(taskId)}/chat/messages/${messageId}/reactions`,
    { emoji }
  );
  return data;
}

export type AddChatResultResponse = DeepRequired<
  Schema<'tasks_module_internal_models.AddResultFromTextResponse'>
>;

/** POST /tasks/{id}/chat/results — create a task result (multipart/form-data).
 *  Optional `files` go under a repeated `files` key; when present they also
 *  appear in the task chat as a message and in `result.fileIds`. */
export async function addChatResult(
  taskId: string | number,
  text: string,
  files?: File[]
): Promise<AddChatResultResponse> {
  const formData = new FormData();
  formData.append('text', text);
  for (const f of files ?? []) {
    formData.append('files', f, f.name);
  }
  const { data } = await http.post<AddChatResultResponse>(
    `/tasks/${encodeURIComponent(taskId)}/chat/results`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  return data;
}

export type BulkToChecklistResponse = WithOptional<
  DeepRequired<Schema<'tasks_module_internal_models.BulkToChecklistResponse'>>,
  'itemIds'
>;

/** POST /tasks/{id}/chat/messages/bulk-to-checklist — add the selected
 *  messages' text as checklist items. Pass `checklistId` to append to an
 *  existing checklist, or `newChecklistTitle` to create one on the fly
 *  (exactly one of the two is required — both empty is a 400). */
export async function bulkMessagesToChecklist(
  taskId: string | number,
  messageIds: number[],
  target: { checklistId?: number; newChecklistTitle?: string }
): Promise<BulkToChecklistResponse> {
  const { data } = await http.post<BulkToChecklistResponse>(
    `/tasks/${encodeURIComponent(taskId)}/chat/messages/bulk-to-checklist`,
    {
      messageIds,
      checklistId: target.checklistId ?? 0,
      newChecklistTitle: target.newChecklistTitle ?? '',
    }
  );
  return data;
}

/** POST /tasks/{id}/chat/messages/delete — bulk delete. Per-message failures
 *  don't abort the rest; the response reports both sets. */
export async function bulkDeleteChatMessages(
  taskId: string | number,
  ids: number[]
): Promise<BulkDeleteResult> {
  const { data } = await http.post<BulkDeleteResult>(
    `/tasks/${encodeURIComponent(taskId)}/chat/messages/delete`,
    { ids }
  );
  return data;
}
